import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import Modal from 'react-modal';
import { Link, useNavigate } from 'react-router-dom';
import { ModalBase } from '../../components/ModalBase';
import { routes } from '../../routes/routeConfig';
import { emailService } from '../../services/EmailService';
import UsersService from '../../services/UsersService';
import { encodeToBase64Url } from '../../utils/base64Url';
import { isValidEmailWithCommonTLD } from '../../utils/emailValidation';
import { buildActivationEmail } from '../activateAccount/EmailTemplate';
// Configurar el root para react-modal
if (typeof document !== 'undefined') {
    Modal.setAppElement('#root');
}

interface ActivationTokenPayload {
    email: string;
    idUsuario?: number | null;
}

const MIN_REGISTER_AGE = Number(process.env.REACT_APP_MIN_REGISTER_AGE) || 9;

const getMinBirthDate = (): Date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const threshold = new Date(today);
    threshold.setFullYear(threshold.getFullYear() - MIN_REGISTER_AGE);
    return threshold;
};

const parseDateInputValue = (value: string): Date => {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
        return new Date(NaN);
    }
    return new Date(year, month - 1, day);
};

const formatDateForInput = (date: Date): string => {
    const cloned = new Date(date);
    cloned.setMinutes(cloned.getMinutes() - cloned.getTimezoneOffset());
    return cloned.toISOString().slice(0, 10);
};

const generateActivationToken = ({ email, idUsuario = null }: ActivationTokenPayload): string => {
    const payload: { email: string; id_usuario?: number } = {
        email: email.trim(),
    };

    if (typeof idUsuario === 'number' && Number.isFinite(idUsuario)) {
        payload.id_usuario = idUsuario;
    }

    return encodeToBase64Url(JSON.stringify(payload));
};

const RegisterPage = () => {
    const navigate = useNavigate();
    const [form, setForm] = useState({
        nickname: '',
        birthdate: '',
        email: '',
        password: '',
        confirmPassword: '',
        parental_consent: false,
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [success, setSuccess] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [loading, setloading] = useState(false);

    // Validación de un solo campo (más exhaustiva)
    const validateField = (field: string, value: any) => {
        let error = '';
        switch (field) {
            case 'nickname': {
                if (!value || value.length < 3) {
                    error = 'El apodo debe tener al menos 3 caracteres';
                } if (value && value.length > 20) {
                    error = 'El Nombre o Nickname no puede exceder los 20 caracteres';
                } else if (!/^[A-Za-zÁÉÍÓÚáéíóúÑñ0-9 ]+$/.test(value)) {
                    error = 'Solo letras, números y tildes son permitidos';
                }
                break;
            }
            case 'birthdate': {
                if (!value) {
                    error = 'La fecha de nacimiento es obligatoria';
                } else {
                    const date = parseDateInputValue(value);
                    if (isNaN(date.getTime())) {
                        error = 'Fecha inválida';
                    } else {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date > today) {
                            error = 'La fecha no puede ser futura';
                        } else {
                            const minimumBirthDate = getMinBirthDate();
                            if (date > minimumBirthDate) {
                                error = `Debes tener al menos ${MIN_REGISTER_AGE} años`;
                            }
                        }
                    }
                }
                break;
            }
            case 'email': {
                if (!value || !isValidEmailWithCommonTLD(value)) {
                    error = 'Correo electrónico no válido';
                }
                break;
            }
            case 'password': {
                if (!value || value.length < 8) {
                    error = 'La contraseña debe tener al menos 8 caracteres';
                } else if (!value || value.length > 25) {
                    error = 'La contraseña no puede exceder los 25 caracteres';
                } else if (!/(?=.*[A-Z])(?=.*[a-z])(?=.*\d)/.test(value)) {
                    error = 'Debe contener mayúsculas, minúsculas y números';
                } else if (!/(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~¡¿])/.test(value)) {
                    error = 'Debe contener al menos un caracter especial';
                }
                break;
            }
            case 'confirmPassword': {
                if (value !== form.password) {
                    error = 'Las contraseñas no coinciden';
                }
                break;
            }
            case 'parental_consent': {
                if (!value) {
                    error = 'Debes aceptar los términos y condiciones';
                }
                break;
            }
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: error }));
        return error === '';
    };




    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        // Aplica trim a todos los campos de texto, email y password
        let fieldValue: any;
        if (type === 'checkbox') {
            fieldValue = checked;
        } else if (type === 'text' || type === 'email' || type === 'password') {
            fieldValue = value.trim();
            if (type === 'email') {
                fieldValue = fieldValue.toLowerCase();
            }
        } else {
            fieldValue = value;
        }
        setForm(prev => {
            const updated = { ...prev, [name]: fieldValue };
            return updated;
        });
        validateField(name, fieldValue);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSuccess('');
        setSubmitError('');

        try {
            setloading(true);
            const hasValidationErrors = Object.values(errors).some(Boolean);
            const isFormComplete = Boolean(
                form.nickname &&
                form.birthdate &&
                form.email &&
                form.password &&
                form.confirmPassword &&
                form.parental_consent
            );

            if (hasValidationErrors || !isFormComplete) {
                setloading(false);
                return;
            }
            const { nickname, birthdate, email, password, parental_consent } = form;
            const res = await UsersService.createUser({ nickname, fecha_nacimiento: birthdate, email, password, parental_consent });
            if (res.statusCode === 201 || res.statusCode === 200) {
                setSuccess('Enviaremos un correo de verificación a tu email. Por favor, revisa tu bandeja de entrada y la carpeta de spam.');
                const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://ciclii.cl';
                const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                const userId = res.usuario?.id_usuario;
                const activationToken = generateActivationToken({ email, idUsuario: typeof userId === 'number' ? userId : null });
                const activationLink = `${normalizedBaseUrl}/activar-cuenta?activation=${activationToken}`;
                await emailService.sendEmail(
                    {
                        to: email,
                        subject: 'Activa tu cuenta en CICLII',
                        html: buildActivationEmail({ nickname, activationLink }),
                    }
                );
                setForm({ nickname: '', birthdate: '', email: '', password: '', confirmPassword: '', parental_consent: false });
            } else {
                setSubmitError(res.message || 'Por favor intenta nuevamente');
            }
        } catch (err: any) {
            setSubmitError(err?.response?.data?.message || 'Por favor intenta nuevamente');
        } finally {
            setloading(false);
        }
    };

    // Modal para mensajes
    const closeModal = (redirect?: boolean) => {
        setSuccess('');
        setSubmitError('');
        if (redirect) {
            navigate('/');
        }
    };

    return (
        <div className='landing-container'>
            <div className='container'>
                <div className='header'>
                    <h1>✨ Regístrate en CICLII</h1>
                    <p>Completa el formulario para crear tu cuenta</p>
                </div>

                <form id='registrationForm' onSubmit={handleSubmit} noValidate autoComplete='off'>
                    <div className='form-group'>
                        <label htmlFor='nickname'>Nombre o Nickname</label>
                        <input
                            className='input'
                            type='text'
                            id='nickname'
                            name='nickname'
                            required
                            placeholder='MiApodo123'
                            value={form.nickname}
                            onChange={handleChange}
                            autoComplete='new-nickname'
                            maxLength={21}
                        />
                        {errors.nickname && <div className='error-message' style={{ margin: 0, paddingBottom: 0 }}>{errors.nickname}</div>}
                    </div>

                    <div className='form-group'>
                        <label htmlFor='birthdate'>Fecha de Nacimiento</label>
                        {(() => {
                            const maxDate = formatDateForInput(getMinBirthDate());
                            return (
                                <input
                                    className='input'
                                    type='date'
                                    id='birthdate'
                                    name='birthdate'
                                    required
                                    value={form.birthdate}
                                    onChange={handleChange}
                                    max={maxDate}
                                    autoComplete='new-birthday'
                                />
                            );
                        })()}
                        {errors.birthdate && <div className='error-message' style={{ margin: 0, paddingBottom: 0 }}>{errors.birthdate}</div>}
                    </div>

                    <div className='form-group'>
                        <label htmlFor='email'>Correo Electrónico</label>
                        <input
                            className='input'
                            type='email'
                            id='email'
                            name='email'
                            required
                            placeholder='tu@email.com'
                            value={form.email}
                            onChange={handleChange}
                            autoComplete='new-email'
                        />
                        {errors.email && <div className='error-message' style={{ margin: 0, paddingBottom: 0 }}>{errors.email}</div>}
                    </div>


                    <div className='form-group'>
                        <label htmlFor='password'>Contraseña</label>
                        <div className='password-disclaimer'>La contraseña debe contener entre 8 y 25 caracteres, mayúsculas, minúsculas y al menos un caracter especial.</div>
                        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <input
                                className='input'
                                type={showPassword ? 'text' : 'password'}
                                id='password'
                                name='password'
                                required
                                placeholder='••••••••'
                                value={form.password}
                                onChange={handleChange}
                                maxLength={26}
                                style={{ flex: 1 }}
                                autoComplete='new-password'
                            />
                            <button
                                type='button'
                                tabIndex={-1}
                                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                onClick={() => setShowPassword(v => !v)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    color: '#B57EDC',
                                    fontSize: 20,
                                    position: 'absolute',
                                    right: 12,
                                    top: '50%',
                                    transform: 'translateY(-48%)',
                                    zIndex: 2,
                                }}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                        {errors.password && <div className='error-message' style={{ margin: 0, paddingBottom: 0 }}>{errors.password}</div>}
                    </div>
                    <div className='form-group'>
                        <label htmlFor='confirmPassword'>Confirmación de Contraseña</label>
                        <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                            <input
                                className='input'
                                type={showConfirm ? 'text' : 'password'}
                                id='confirmPassword'
                                name='confirmPassword'
                                required
                                placeholder='••••••••'
                                value={form.confirmPassword}
                                onChange={handleChange}
                                maxLength={26}
                                style={{ flex: 1 }}
                                autoComplete='new-password'
                            />
                            <button
                                type='button'
                                tabIndex={-1}
                                aria-label={showConfirm ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                onClick={() => setShowConfirm(v => !v)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    color: '#B57EDC',
                                    fontSize: 20,
                                    position: 'absolute',
                                    right: 12,
                                    top: '50%',
                                    transform: 'translateY(-48%)',
                                    zIndex: 2,
                                }}
                            >
                                {showConfirm ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                        {errors.confirmPassword && <div className='error-message' style={{ margin: 0, paddingBottom: 0 }}>{errors.confirmPassword}</div>}
                    </div>

                    <div className='custom-checkbox-group'>
                        <label className='custom-checkbox-label' htmlFor='parental_consent'>
                            <input type='checkbox' id='parental_consent' name='parental_consent' required checked={form.parental_consent} onChange={handleChange} />
                            <span className='custom-checkbox'></span>
                            <span className='label-text' style={{ marginLeft: 8 }}>
                                Consentimiento parental. He leído y acepto los
                                <Link
                                    to={routes.public.terms}
                                    className='parental_consent-link'
                                    style={{ color: '#B57EDC', textDecoration: 'underline', marginLeft: 4 }}
                                >
                                    Términos y Condiciones
                                </Link>
                                {' '}y la
                                <Link
                                    to={routes.public.privacy}
                                    className='parental_consent-link'
                                    style={{ color: '#B57EDC', textDecoration: 'underline', marginLeft: 4 }}
                                >
                                    Política de Privacidad
                                </Link>
                            </span>
                        </label>
                        {errors.parental_consent && <div className='error-message' style={{ margin: 0 }}>{errors.parental_consent}</div>}
                    </div>

                    <button
                        type='submit'
                        className={`btn btn-primary btn-block${loading ? ' btn-loading' : ''}`}
                        disabled={loading || Object.values(errors).some(Boolean) || !(
                            form.nickname &&
                            form.birthdate &&
                            form.email &&
                            form.password &&
                            form.confirmPassword &&
                            form.parental_consent
                        )}
                    >
                        <span style={{ visibility: loading ? 'hidden' : 'visible' }}>Crear Cuenta</span>
                    </button>

                    <p className='signup-link'>
                        ¿Ya tienes una cuenta? <button type='button' onClick={() => navigate('/')}>Inicia sesión</button>
                    </p>
                </form>

                {/* Modal para mensajes de éxito o error */}


                <ModalBase
                    isOpen={!!success}
                    onRequestClose={() => closeModal(true)}
                    title='¡Registro exitoso!'
                    type='success'
                    footerButtons={[
                        <button key='login' className='btn btn-primary' onClick={() => closeModal(true)}>Iniciar sesión</button>,
                    ]}
                >
                    <div>
                        {success}
                    </div>
                </ModalBase>
                <ModalBase
                    isOpen={!!submitError}
                    onRequestClose={closeModal}
                    title='Ha ocurrido un error'
                    type='error'
                    footerButtons={[
                        <button key='cerrar' className='btn btn-secondary' onClick={() => closeModal()}>Ok</button>,
                    ]}
                >
                    {submitError}
                </ModalBase>
            </div>
        </div>
    );
};

export default RegisterPage;